import { useDisclosure } from '@chakra-ui/react'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PropertiesContext,
  PropertiesContextType,
} from '../../../../contexts/PropertiesContext'
import { isUserAdmin } from '../../../../lib/supabase'
import type { Property } from '../../../../types'
import ConfirmationDialog from '../../../ui/ConfirmationDialog'
import PropertyCardUI from './PropertyCardUI'
import type { PropertyCardProps } from './types'

const PropertyCard: React.FC<PropertyCardProps> = ({ property, onClick }) => {
  const { handlePropertyClick, softDelete, restore, isDeleting, isRestoring } =
    useContext<PropertiesContextType | undefined>(
      PropertiesContext
    ) as PropertiesContextType
  const navigate = useNavigate()
  const [isAdmin, setIsAdmin] = useState(false)
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null
  )
  const [dialogType, setDialogType] = useState<'delete' | 'restore' | null>(
    null
  )

  const {
    isOpen: isDialogOpen,
    onOpen: onDialogOpen,
    onClose: onDialogClose,
  } = useDisclosure()

  useEffect(() => {
    const checkAdminStatus = async () => {
      const adminStatus = await isUserAdmin()
      setIsAdmin(adminStatus)
    }
    checkAdminStatus()
  }, [])

  const handleClick = () => {
    if (onClick) {
      onClick(property.id)
    } else {
      handlePropertyClick(property.id)
    }
  }

  const handleEdit = (property: Property) => {
    // Navigate to edit page
    navigate(`/properties/edit/${property.id}`)
  }

  const handleDelete = (property: Property) => {
    setSelectedProperty(property)
    setDialogType('delete')
    onDialogOpen()
  }

  const handleRestore = (property: Property) => {
    setSelectedProperty(property)
    setDialogType('restore')
    onDialogOpen()
  }

  const handleConfirmAction = () => {
    if (!selectedProperty || !dialogType) return

    if (dialogType === 'delete') {
      softDelete(selectedProperty.id)
    } else if (dialogType === 'restore') {
      restore(selectedProperty.id)
    }
  }

  const handleCloseDialog = () => {
    onDialogClose()
    setSelectedProperty(null)
    setDialogType(null)
  }

  const getDialogConfig = () => {
    if (!selectedProperty || !dialogType) return null

    if (dialogType === 'delete') {
      return {
        title: 'Confirmar Exclusão',
        message: `Tem certeza que deseja excluir a acomodação "${selectedProperty.title}"?`,
        confirmText: 'Excluir',
        confirmColorScheme: 'red',
        isLoading: isDeleting,
      }
    } else {
      return {
        title: 'Confirmar Restauração',
        message: `Tem certeza que deseja restaurar a acomodação "${selectedProperty.title}"?`,
        confirmText: 'Restaurar',
        confirmColorScheme: 'green',
        isLoading: isRestoring,
      }
    }
  }

  const dialogConfig = getDialogConfig()

  return (
    <>
      <PropertyCardUI
        property={property}
        onClick={handleClick}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRestore={isAdmin ? handleRestore : undefined}
        isDeleted={!!property.deleted_at}
        inLine={false}
      />

      {dialogConfig && (
        <ConfirmationDialog
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
          onConfirm={handleConfirmAction}
          title={dialogConfig.title}
          message={dialogConfig.message}
          confirmText={dialogConfig.confirmText}
          confirmColorScheme={dialogConfig.confirmColorScheme}
          isLoading={dialogConfig.isLoading}
        />
      )}
    </>
  )
}

export default PropertyCard
